import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { APIURL, TZ } from '../../../Global';
import { useAlert } from 'react-alert';
import { optionsList } from '../AddMedicines/formOptions';
import { sampleList } from './sampleList';
import TestParameterRow from './TestParameterRow';
import CustomSelect from '../AddMedicines/CustomSelect';
import { IoCopyOutline } from "react-icons/io5";

const TestDetails = ({ type, selected, selectedTest, selectedTestGroup, handleSuccess, sample, data }) => {
    const [testData, setTestData] = useState({
        id: '',
        sample: '',
        name: '',
        unit: '',
        note: '',
        input_type: '',
    });

    const [refRange, setRefRange] = useState([]);

    const [panels, setPanels] = useState([]);
    const [tests, setTests] = useState([]);
    const [error, setError] = useState({
        sample: '',
        panels: '',
        tests: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const alert = useAlert();

    // ── Machine states ────────────────────────────────────────────────────────
    const [machineOptions, setMachineOptions] = useState([]);
    const [machineSearch, setMachineSearch] = useState('');
    const [machineLoading, setMachineLoading] = useState(false);
    const [selectedMachine, setSelectedMachine] = useState(null);
    const machineSearchTimeout = useRef(null);
    // Stores the machine to re-apply after a save-triggered data refresh
    const pendingMachineRef = useRef(null);

    const samples = useMemo(() =>
        sampleList?.map((item) => ({ value: item, label: item })) || [],
        []
    );

    const panelsList = useMemo(() =>
        data?.panels?.map((panel) => ({
            value: panel.id,
            label: panel.panel_name,
        })) || [],
        [data?.panels]
    );

    const testsList = useMemo(() =>
        data?.tests?.map((test) => ({
            value: test.id,
            label: test.test_name,
        })) || [],
        [data?.tests]
    );

    // ── Fetch machines ONCE, filter client-side ───────────────────────────────
    const allMachinesRef = useRef([]);  // holds the full unfiltered list

    const fetchMachines = useCallback(() => {
        setMachineLoading(true);
        const tokenString = sessionStorage.getItem('usertoken');
        const token = tokenString ? tokenString.replace(/["]+/g, '') : '';

        axios
            .get(`${APIURL}/api/v1/staff/machine-view/`, {
                headers: { Authorization: `Token ${token}` },
            })
            .then((res) => {
                if (res.data?.status === 'success') {
                    const machines = res.data.machines || [];
                    allMachinesRef.current = machines;         // cache full list
                    setMachineOptions(
                        machines.map((m) => ({
                            value: m.id,
                            label: `${m.machine_code} – ${m.machine_name}`,
                            machine: m,
                        }))
                    );
                }
            })
            .catch((err) => console.error('Failed to fetch machines:', err))
            .finally(() => setMachineLoading(false));
    }, []);

    // Load machines once when the test view is active
    useEffect(() => {
        if (selected === 'test') fetchMachines();
    }, [selected, fetchMachines]);

    // Filter client-side on every keystroke — NO extra API call
    const handleMachineInputChange = (inputValue) => {
        setMachineSearch(inputValue);
        const q = inputValue.toLowerCase();
        const filtered = allMachinesRef.current.filter(
            (m) =>
                !q ||
                m.machine_name.toLowerCase().includes(q) ||
                m.machine_code.toLowerCase().includes(q)
        );
        setMachineOptions(
            filtered.map((m) => ({
                value: m.id,
                label: `${m.machine_code} – ${m.machine_name}`,
                machine: m,
            }))
        );
    };

    // Helper: normalise a reference_parameter entry to the internal format
    const normaliseRef = (r) => ({
        category: r.category || '',
        min_value: r.min !== undefined && r.min !== null ? String(r.min) : (r.min_value != null ? String(r.min_value) : ''),
        max_value: r.max !== undefined && r.max !== null ? String(r.max) : (r.max_value != null ? String(r.max_value) : ''),
    });

    // Helper: build parameter rows from raw test_parameters (global view)
    const buildGlobalParams = (testParameters = []) =>
        testParameters.map((param) => ({
            id: param.id,
            selectedOption: { value: param.test_parameter, label: param.test_parameter },
            unit: param.unit || '',
            order: param.order?.toString() || '',
            value: param.input_type || '',
            note: param.note || '',
            reference_parameter: (param.reference_parameter || []).map(normaliseRef),
            sub_tests: (param.sub_parameters || []).map((sub) => ({
                id: sub.id,
                selectedOption: {
                    value: sub.name || sub.test_parameter || '',
                    label: sub.name || sub.test_parameter || '',
                },
                unit: sub.unit || '',
                order: sub.order?.toString() || '',
                value: sub.value || sub.input_type || '',
                note: sub.note || '',
                calculation_type: sub.calculation_type || '',
                reference_parameter: (sub.ref_range || sub.reference_parameter || []).map(normaliseRef),
            })),
        }));

    // Helper: build parameter rows for a machine context (only machine-specific values)
    const buildMachineParams = (testParameters = [], machineParams = []) =>
        testParameters.map((param) => {
            const mParam = machineParams.find(mp => mp.parameter_id === param.id);
            const paramName = mParam?.parameter_name || param.test_parameter;
            return {
                id: param.id,
                selectedOption: { value: paramName, label: paramName },
                unit: mParam?.unit || '',
                order: (mParam?.order ?? param.order ?? 0).toString(),
                value: mParam?.input_type || '',
                note: mParam?.note || '',
                reference_parameter: (mParam?.reference_parameter || []).map(normaliseRef),
                sub_tests: (mParam?.sub_parameters || []).map((mSub) => {
                    // Try to find the original global sub-parameter name/context if needed, 
                    // but primarily use machine-provided values.
                    const subName = mSub.name || mSub.parameter_name || '';
                    return {
                        id: mSub.parameter_id || mSub.id,
                        selectedOption: {
                            value: subName,
                            label: subName,
                        },
                        unit: mSub.unit || '',
                        order: (mSub.order ?? 0).toString(),
                        value: mSub.input_type || '',
                        note: mSub.note || '',
                        calculation_type: mSub.calculation_type || '',
                        reference_parameter: (mSub.reference_parameter || []).map(normaliseRef),
                    };
                }),
            };
        });

    // Machine dropdown change handler
    const handleMachineChange = (option) => {
        setSelectedMachine(option);

        if (!selectedTest) return;

        // ── Machine CLEARED → restore global test data ────────────────────────
        if (!option) {
            setTestData((prev) => ({
                ...prev,
                unit: selectedTest.unit || '',
                input_type: selectedTest.input_type || '',
                note: selectedTest.note || '',
            }));

            setRefRange(
                (selectedTest.reference_parameter || []).map(normaliseRef)
            );

            // Filter out params that are exclusively machine-specific
            const machineParamIds = new Set(
                (selectedTest.machines || []).flatMap(m =>
                    (m.parameters || []).map(p => p.parameter_id)
                )
            );
            const globalTestParams = (selectedTest.test_parameters || []).filter(
                param => !machineParamIds.has(param.id)
            );
            setTests(buildGlobalParams(globalTestParams));
            return;
        }

        // ── Machine SELECTED → load machine-specific data ─────────────────────
        const machineData = (selectedTest.machines || []).find(
            (m) => m.machine_id === option.value
        );

        if (!machineData) {
            // Machine exists in the dropdown but no saved data for this test yet
            // Show NOTHING for parameters so "Add New Parameter" button appears
            setTestData((prev) => ({
                ...prev,
                unit: '',
                input_type: '',
                note: '',
            }));
            setRefRange([]);
            setTests([]);
            return;
        }

        // Update top-level test fields from machine (only use machine values)
        setTestData((prev) => ({
            ...prev,
            unit: machineData.unit || '',
            input_type: machineData.input_type || '',
            note: machineData.note || '',
        }));

        // Machine-level reference ranges
        setRefRange((machineData.reference_parameter || []).map(normaliseRef));

        // Machine-level parameters - only show parameters explicitly configured for this machine
        const mParams = machineData.parameters || [];
        if (mParams.length > 0) {
            setTests(buildMachineParams(selectedTest.test_parameters || [], mParams).filter(tp =>
                mParams.some(mp => mp.parameter_id === tp.id)
            ));
        } else {
            setTests([]);
        }
    };

    // Restore machine selection after data refresh, or clear it when a different test is selected
    useEffect(() => {
        if (pendingMachineRef.current) {
            // A save just happened — reapply the same machine to rebuild the machine view
            const machineToRestore = pendingMachineRef.current;
            pendingMachineRef.current = null;
            // Use a microtask so selectedTest state is fully settled first
            setTimeout(() => {
                handleMachineChange(machineToRestore);
            }, 0);
        } else {
            setSelectedMachine(null);
        }
    }, [selectedTest]);

    useEffect(() => {
        if (selected === 'testgroup' && selectedTestGroup) {
            setTestData({
                id: selectedTestGroup.value || '',
                sample: '',
                name: selectedTestGroup.label || '',
                unit: '',
                note: selectedTest?.note || '',
                input_type: '',
            });

            if (selectedTest?.panels) {
                const selectedPanelIds = selectedTest.panels.map(panel => panel.id);
                const selectedPanels = data.panels
                    .filter(panel => selectedPanelIds.includes(panel.id))
                    .map(panel => ({
                        value: panel.id,
                        label: panel.panel_name,
                    }));
                setPanels(selectedPanels);
            }

            if (selectedTest?.tests) {
                const selectedTestIds = selectedTest.tests.map(test => test.id);
                const selectedTests = data.tests
                    .filter(test => selectedTestIds.includes(test.id))
                    .map(test => ({
                        value: test.id,   // FIX: single value key (was duplicated)
                        id: test.id,
                        label: test.test_name,
                        selectedOption: { value: test.test_name, label: test.test_name },
                        unit: test.unit || '',
                        order: test.order || '',
                        input_type: test.input_type || '',  // FIX: renamed from duplicate 'value'
                        note: test.note || '',
                        reference_parameter: (test.reference_parameter && test.reference_parameter.length > 0)
                            ? test.reference_parameter
                            : []
                    }));
                setTests(selectedTests);
            } else {
                setTests([]);
            }

            setRefRange([]);

        } else if (selectedTest) {
            setTestData({
                id: selectedTest.id || '',
                sample: selectedTest.sample || selectedTest.sample_type || sample || '',
                name: selectedTest.test_name || selectedTest.panel_name || '',
                unit: selectedTest.unit || '',
                note: selectedTest.note || '',
                input_type: selectedTest.input_type || '',
            });

            if (selectedTest.reference_parameter && Array.isArray(selectedTest.reference_parameter) && selectedTest.reference_parameter.length > 0) {
                setRefRange(selectedTest.reference_parameter);
            } else {
                setRefRange([]);
            }

            if (selected === 'panels' && selectedTest.id && data?.tests) {
                const filteredTests = data.tests.filter((test) =>
                    test?.related_panel_ids?.includes(selectedTest.id)
                );

                setTests(
                    filteredTests.map((test) => ({
                        value: test.id,       // FIX: single value key (was duplicated)
                        id: test.id,          // FIX: explicit id field
                        label: test.test_name,
                        selectedOption: { value: test.test_name, label: test.test_name },
                        unit: test.unit || '',
                        order: test.order || '',
                        input_type: test.input_type || '',  // FIX: renamed from duplicate 'value'
                        note: test.note || '',
                        reference_parameter: (test.reference_parameter && test.reference_parameter.length > 0)
                            ? test.reference_parameter
                            : []
                    }))
                );
                setPanels([]);

            } else if (selected === 'test' && selectedTest.related_panel_ids && data?.panels) {
                const relatedPanels = selectedTest.related_panel_ids
                    .map((panelId) => {
                        const panel = data.panels.find((panel) => panel.id === panelId);
                        return panel ? { value: panelId, label: panel.panel_name } : null;
                    })
                    .filter(Boolean);
                setPanels(relatedPanels);

                if (selectedTest.test_parameters && Array.isArray(selectedTest.test_parameters)) {
                    // Collect all parameter IDs that are machine-specific only
                    const machineParamIds = new Set(
                        (selectedTest.machines || []).flatMap(m =>
                            (m.parameters || []).map(p => p.parameter_id)
                        )
                    );

                    // A parameter is global if its ID is NOT exclusively a machine param.
                    // A param is "exclusively machine" if it appears in machine params
                    // but its ID was NOT present as a top-level test_parameter before
                    // machine assignment. We detect this by checking if the param
                    // exists in machines but NOT in the global test_parameters from
                    // the perspective of being added without a machine.
                    // 
                    // Safest rule: only exclude a test_parameter if its ID appears
                    // in machine params BUT NOT in the list of IDs that are exclusively
                    // added globally (i.e., added without any machine context).
                    // Since the API merges all, we filter: keep a test_parameter if
                    // its ID does NOT appear in any machine's parameters list,
                    // OR if it appears in multiple machines (shared global param).
                    const machineParamIdArr = (selectedTest.machines || []).flatMap(m =>
                        (m.parameters || []).map(p => p.parameter_id)
                    );
                    // Count how many machines reference each param id
                    const idCounts = machineParamIdArr.reduce((acc, id) => {
                        acc[id] = (acc[id] || 0) + 1;
                        return acc;
                    }, {});

                    const totalMachines = (selectedTest.machines || []).length;
                    const globalTestParams = selectedTest.test_parameters.filter(param => {
                        // If this param id is not referenced by ANY machine, it's global
                        if (!machineParamIds.has(param.id)) return true;
                        // If it's referenced by machines but fewer than all machines,
                        // it's machine-specific — exclude from global view
                        return false;
                    });

                    setTests(buildGlobalParams(globalTestParams));
                } else {
                    setTests([]);
                }
            }
        }

        setError({ sample: '', panels: '', tests: '' });
    }, [selectedTest, selectedTestGroup, data?.panels, data?.tests, sample, selected]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTestData((current) => ({
            ...current,
            [name]: value,
        }));
        if (name === 'sample') {
            setError((current) => ({ ...current, sample: '' }));
        }
    };

    const handleSampleChange = (selectedOption) => {
        const value = selectedOption ? selectedOption.value : '';
        setTestData((current) => ({
            ...current,
            sample: value,
        }));
        setError((current) => ({ ...current, sample: '' }));
    };

    const handlePanelChange = (selectedOptions) => {
        setPanels(selectedOptions || []);
        setError((current) => ({ ...current, panels: '' }));
    };

    const handleTestChange = (selectedOptions) => {
        setTests(
            selectedOptions?.map(opt => ({
                ...opt,
                id: opt.value,   // FIX: ensure id is always set from value
            })) || []
        );
        setError((current) => ({ ...current, tests: '' }));
    };

    const validateForm = () => {
        const newErrors = { sample: '', panels: '', tests: '' };
        let isValid = true;

        if (selected === 'test') {
            const invalidRanges = refRange.some(range => {
                const hasSomeFields = range.category || range.min_value || range.max_value;
                const hasAllFields = range.category && range.min_value && range.max_value;
                return hasSomeFields && !hasAllFields;
            });

            if (invalidRanges) {
                alert.error('Please complete all fields for each reference range (category, min, and max values)');
                isValid = false;
            }

            const missingParameterName = tests.some(test => !test.selectedOption || !test.selectedOption.value);
            if (missingParameterName) {
                alert.error("Please provide a name for all parameters.");
                isValid = false;
            }

            const missingSubParameterName = tests.some(test =>
                test.sub_tests && test.sub_tests.some(sub => !sub.selectedOption || !sub.selectedOption.value)
            );
            if (missingSubParameterName) {
                alert.error("Please provide a name for all subparameters.");
                isValid = false;
            }
        }

        setError(newErrors);
        return isValid;
    };

    const handleSubmit = () => {
        if (!validateForm()) return;

        setIsLoading(true);
        let basePayload = {};

        if (selected === 'testgroup') {
            basePayload = {
                group_id: testData.id,
                test_ids: tests?.map((test) => test.value?.toString()) ?? [],
                panel_ids: panels?.map((panel) => panel.value?.toString()) ?? [],
                note: testData.note || ''
            };

        } else if (selected === 'test') {
            // Base test payload — machine is added only when selected
            basePayload = {
                test_id: selectedTest.id,
                test_name: testData.name,
                note: testData.note || '',
                panel_ids: panels.map((panel) => panel.value),
                unit: testData.unit,
                sample: testData.sample || '', // Ensure it passes "" instead of null/undefined
                sample_type: testData.sample || '', // Pass sample_type only if available
                input_type: testData.input_type || '',
                reference_parameter: refRange.filter(range =>
                    range.category && range.min_value && range.max_value
                ),
                parameters: tests.map((test, index) => ({
                    id: test.id,
                    test_parameter: test.selectedOption?.value || '',
                    input_type: test.value,
                    is_required: true,
                    unit: test.unit || '',
                    order: parseInt(test.order) || index + 1,
                    note: test.note || '',
                    reference_parameter: (test.reference_parameter || []).filter(range =>
                        range.category && range.min_value && range.max_value
                    ),
                    sub_parameters: (test.sub_tests || []).map((sub, subIndex) => ({
                        name: sub.selectedOption?.value || '',
                        input_type: sub.value,
                        unit: sub.unit || '',
                        order: parseInt(sub.order) || subIndex + 1,
                        calculation_type: sub.calculation_type || '',
                        note: sub.note || '',
                        reference_parameter: (sub.reference_parameter || []).filter(range =>
                            range.category && range.min_value && range.max_value
                        ),
                    }))
                }))
            };

            // If a machine is selected, attach machine-specific fields
            if (selectedMachine) {
                basePayload.machine = selectedMachine.value;
            }

        } else if (selected === 'panels') {
            // FIX: dedicated panels payload with all required fields
            basePayload = {
                panel_id: selectedTest.id,
                panel_name: testData.name,
                note: testData.note || '',
                sample_type: testData.sample,   // FIX: include sample_type
                test_ids: tests
                    .map((test) => test.id || test.value)
                    .filter(id => id !== '' && id !== null && id !== undefined),
            };
        }

        const tokenString = sessionStorage.getItem('usertoken');
        if (!tokenString) {
            alert.error('Authentication token not found. Please login again.');
            setIsLoading(false);
            return;
        }

        const token = tokenString.replace(/["]+/g, '');

        const apiEndpoint = selected === 'testgroup'
            ? `${APIURL}/api/v1/staff/master-test-group/?${TZ}`
            : `${APIURL}/api/v1/staff/lab-test/?${TZ}`;

        axios
            .put(apiEndpoint, basePayload, {
                headers: {
                    Authorization: `Token ${token}`,
                    'Content-Type': 'application/json',
                },
            })
            .then((res) => {
                setIsLoading(false);
                if (res.status === 200 && res.data?.status === 'success') {
                    alert.success('Updated successfully!');

                    if (res.data.existed_test && res.data.existed_test.length > 0) {
                        alert.info(
                            `The following tests already exist and were not added: ${res.data.existed_test.join(', ')}`,
                            { timeout: 10000 }
                        );
                    }
                    if (res.data.existed_panel && res.data.existed_panel.length > 0) {
                        alert.info(
                            `The following panels already exist and were not added: ${res.data.existed_panel.join(', ')}`,
                            { timeout: 10000 }
                        );
                    }

                    if (handleSuccess && typeof handleSuccess === 'function') {
                        // Preserve the machine selection across the data refresh
                        if (selected === 'test' && selectedMachine) {
                            pendingMachineRef.current = selectedMachine;
                        }
                        handleSuccess();
                    }
                } else {
                    alert.error(res.data?.message || `Failed to update ${selected}.`);
                }
            })
            .catch((err) => {
                setIsLoading(false);
                console.error('Update error:', err);
                const errorMessage = err.response?.data?.message ||
                    err.response?.data?.error ||
                    `Failed to update ${selected}. Please try again.`;
                alert.error(errorMessage);
            });
    };

    if (!selectedTest && !selectedTestGroup) {
        return <div>No item selected</div>;
    }

    const getInputLabel = () => {
        if (selected === 'testgroup') return 'Test Group Name';
        if (selected === 'test') return 'Test Name';
        return 'Panel Name';
    };

    const addTestRow = () => {
        let nextOrder = 0;
        const existingOrders = tests.map(t => parseInt(t.order)).filter(num => !isNaN(num));
        if (existingOrders.length > 0) nextOrder = Math.max(...existingOrders) + 1;
        if (nextOrder > 10) nextOrder = '';

        setTests((prev) => [
            ...prev,
            {
                selectedOption: null,
                unit: '',
                note: '',
                order: nextOrder.toString(),
                value: '',
                reference_parameter: [],
                sub_tests: []
            }
        ]);
    };

    const handleAddSubTest = (parentIndex) => {
        const updatedTests = [...tests];
        if (!updatedTests[parentIndex].sub_tests) {
            updatedTests[parentIndex].sub_tests = [];
        }
        updatedTests[parentIndex].sub_tests.push({
            selectedOption: null,
            unit: '',
            note: '',
            order: '',
            value: '',
            reference_parameter: []
        });
        setTests(updatedTests);
    };

    const removeTestRow = async (index, subIndex = null) => {
        const paramToDelete = subIndex !== null ? tests[index].sub_tests[subIndex] : tests[index];

        if (paramToDelete.id) {
            if (!window.confirm("Are you sure you want to delete this parameter?")) return;

            const tokenString = sessionStorage.getItem('usertoken');
            if (!tokenString) {
                alert.error('Authentication token not found. Please login again.');
                return;
            }
            const token = tokenString.replace(/["]+/g, '');

            try {
                // Include machine_id in delete URL when a machine is selected
                const machineQuery = selectedMachine ? `&machine_id=${selectedMachine.value}` : '';
                const res = await axios.delete(
                    `${APIURL}/api/v1/staff/lab-test/?parameter_id=${paramToDelete.id}${machineQuery}&${TZ}`,
                    { headers: { Authorization: `Token ${token}` } }
                );

                if (res.status === 200 && res.data?.status === 'success') {
                    alert.success('Parameter deleted successfully!');
                } else {
                    alert.error(res.data?.message || 'Error deleting parameter');
                    return; // Do not remove from UI if API fails
                }
            } catch (err) {
                console.error('Delete error:', err);
                alert.error(err.response?.data?.message || err.response?.data?.error || 'Failed to delete parameter.');
                return; // Do not remove from UI if API fails
            }
        }

        const updatedTests = [...tests];
        if (subIndex !== null) {
            updatedTests[index].sub_tests = updatedTests[index].sub_tests.filter((_, i) => i !== subIndex);
        } else {
            updatedTests.splice(index, 1);
        }
        setTests(updatedTests);

        // If the parameter was persisted, refresh parent data so the deletion is
        // reflected in the selectedTest prop. Otherwise re-selecting the machine
        // rebuilds the list from stale cached parameters and the deleted row
        // reappears (until a full page refresh refetches the test).
        if (paramToDelete.id && handleSuccess && typeof handleSuccess === 'function') {
            if (selected === 'test' && selectedMachine) {
                pendingMachineRef.current = selectedMachine;
            }
            handleSuccess();
        }
    };

    const handleTestFieldChange = (index, field, value, subIndex = null) => {
        const updatedTests = [...tests];
        if (subIndex !== null) {
            updatedTests[index].sub_tests[subIndex] = { ...updatedTests[index].sub_tests[subIndex], [field]: value };
        } else {
            updatedTests[index] = { ...updatedTests[index], [field]: value };
        }
        setTests(updatedTests);
        if (field === 'selectedOption') setError((prev) => ({ ...prev, tests: '' }));
    };

    const handleNestedRefChange = (testIndex, refIndex, field, value, subIndex = null) => {
        const updatedTests = [...tests];
        if (subIndex !== null) {
            const ranges = [...updatedTests[testIndex].sub_tests[subIndex].reference_parameter];
            ranges[refIndex] = { ...ranges[refIndex], [field]: value };
            updatedTests[testIndex].sub_tests[subIndex].reference_parameter = ranges;
        } else {
            const ranges = [...updatedTests[testIndex].reference_parameter];
            ranges[refIndex] = { ...ranges[refIndex], [field]: value };
            updatedTests[testIndex].reference_parameter = ranges;
        }
        setTests(updatedTests);
    };

    const addNestedRefRange = (testIndex, subIndex = null) => {
        const updatedTests = [...tests];
        const newRef = { category: '', min_value: '', max_value: '' };
        if (subIndex !== null) {
            updatedTests[testIndex].sub_tests[subIndex].reference_parameter.push(newRef);
        } else {
            updatedTests[testIndex].reference_parameter.push(newRef);
        }
        setTests(updatedTests);
    };

    const removeNestedRefRange = (testIndex, refIndex, subIndex = null) => {
        const updatedTests = [...tests];
        if (subIndex !== null) {
            const ranges = updatedTests[testIndex].sub_tests[subIndex].reference_parameter;
            updatedTests[testIndex].sub_tests[subIndex].reference_parameter = ranges.filter((_, i) => i !== refIndex);
        } else {
            const ranges = updatedTests[testIndex].reference_parameter;
            updatedTests[testIndex].reference_parameter = ranges.filter((_, i) => i !== refIndex);
        }
        setTests(updatedTests);
    };

    const handleMainRefChange = (index, field, value) => {
        const updatedRefs = [...refRange];
        updatedRefs[index] = { ...updatedRefs[index], [field]: value };
        setRefRange(updatedRefs);
    };

    const addMainRefRange = () => {
        setRefRange((prev) => [...prev, { category: '', min_value: '', max_value: '' }]);
    };

    const removeMainRefRange = (index) => {
        const updatedRefs = refRange.filter((_, i) => i !== index);
        setRefRange(updatedRefs);
    };

    const specificTestOptions = [
        { value: 'Eosinophils', label: 'Eosinophils' },
        { value: 'Lymphocytes', label: 'Lymphocytes' },
        { value: 'Neutrophils', label: 'Neutrophils' },
        { value: 'Monocytes', label: 'Monocytes' },
        { value: 'MPV', label: 'MPV' },
    ];

    const valueOptions = [
        { value: 'boolean', label: 'Boolean' },
        { value: 'text', label: 'Character' },
        { value: 'numeric', label: 'Numeric' },
    ];

    return (
        <div style={{ height: "454px", overflowY: "auto", padding: "10px" }}>
            <div className="panel-container">

                {/* ── Machine selector (test view only) ── */}
                {selected === 'test' && (
                    <div className='pricing-inputcontainer'>
                        <span>Machine</span>
                        <Select
                            className='input-container'
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    minHeight: '40px',
                                    border: '1px solid #E3E3E3',
                                }),
                                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                menuList: (provided) => ({ ...provided, maxHeight: '200px', zIndex: 9999 }),
                            }}
                            value={selectedMachine}
                            onChange={handleMachineChange}
                            options={machineOptions}
                            onInputChange={handleMachineInputChange}
                            inputValue={machineSearch}
                            placeholder="Select machine..."
                            isClearable
                            isLoading={machineLoading}
                            filterOption={() => true} /* server-side search */
                            noOptionsMessage={() =>
                                machineLoading ? 'Loading...' : 'No machines found'
                            }
                        />
                    </div>
                )}

                <div className='pricing-inputcontainer'>
                    <span>{getInputLabel()}</span>
                    <input
                        name="name"
                        placeholder='Name'
                        value={testData.name}
                        onChange={handleInputChange}
                        disabled={!!selectedMachine || selected === 'panels'}
                    />
                </div>

                {/* FIX: Show sample for both 'test' and 'panels', not just non-testgroup */}
                {(selected === 'test' || selected === 'panels') && (
                    <div className='pricing-inputcontainer'>
                        <span>Sample</span>
                        <Select
                            className='input-container'
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    minHeight: '44px',
                                    border: error.sample ? '1px solid red' : (!!selectedMachine ? '1px solid #f0f0f0' : '1px solid #E3E3E3'),
                                    backgroundColor: !!selectedMachine ? '#f9f9f9' : 'white'
                                }),
                                menu: (provided) => ({
                                    ...provided,
                                    zIndex: 100,
                                }),
                                menuList: (provided) => ({
                                    ...provided,
                                    maxHeight: '200px',
                                    zIndex: 100,
                                }),
                            }}
                            value={samples.find(option => option.value === testData.sample) || null}
                            onChange={handleSampleChange}
                            options={samples}
                            placeholder="Select sample"
                            isClearable={!selectedMachine}
                            isDisabled={!!selectedMachine}
                        />
                        {error.sample && <p style={{ color: 'red', margin: '5px 0 0 0' }}>{error.sample}</p>}
                    </div>
                )}

                {/* Unit, Value, Note - not needed for panels */}
                {selected !== 'panels' && (
                    <>
                        <div className='pricing-inputcontainer'>
                            <span>Unit</span>
                            <input
                                name="unit"
                                placeholder='Unit'
                                value={testData.unit}
                                onChange={handleInputChange}
                                disabled={!!selectedMachine}
                            />
                        </div>

                        <div className='pricing-inputcontainer'>
                            <span>Value</span>
                            <Select
                                className='input-container'
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        minHeight: '40px',
                                        border: '1px solid #E3E3E3',
                                    }),
                                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                    menuList: (provided) => ({ ...provided, maxHeight: '200px', zIndex: 9999 }),
                                }}
                                isClearable
                                isDisabled={!!selectedMachine}
                                options={valueOptions}
                                value={valueOptions.find(opt => opt.value === testData.input_type) || null}
                                onChange={(selectedOption) => setTestData(prev => ({
                                    ...prev,
                                    input_type: selectedOption ? selectedOption.value : ''
                                }))}
                                placeholder="Select Value"
                            />
                        </div>

                        <div className='pricing-inputcontainer'>
                            <span>Note</span>
                            <input
                                name="note"
                                placeholder='Note'
                                value={testData.note}
                                onChange={handleInputChange}
                                disabled={!!selectedMachine}
                            />
                        </div>
                    </>
                )}

                {selected === 'test' && (
                    <>
                        <div className='pricing-inputcontainer'>
                            <span>Reference Range</span>
                            {refRange.map((range, refIndex) => (
                                <div key={refIndex} style={{ display: 'flex', gap: '5px', marginBottom: '10px', alignItems: 'center' }}>
                                    <div style={{ flex: 1, padding: 0 }}>
                                        <CustomSelect
                                            options={optionsList}
                                            placeholder='Category'
                                            value={optionsList?.find(opt => opt.value === range.category) || null}
                                            onChange={(opt) => handleMainRefChange(refIndex, 'category', opt ? opt.value : '')}
                                        />
                                    </div>
                                    <input
                                        className='col-3'
                                        placeholder='Min'
                                        value={range.min_value}
                                        onChange={(e) => handleMainRefChange(refIndex, 'min_value', e.target.value)}
                                    />
                                    <input
                                        className='col-3'
                                        placeholder='Max'
                                        value={range.max_value}
                                        onChange={(e) => handleMainRefChange(refIndex, 'max_value', e.target.value)}
                                    />
                                    {refIndex > 0 && (
                                        <button
                                            type="button"
                                            title="Copy from above"
                                            style={{ cursor: 'pointer', background: 'none', border: 'none', borderRadius: '4px' }}
                                            onClick={() => {
                                                const prevRange = refRange[refIndex - 1];
                                                const updatedRefs = [...refRange];
                                                updatedRefs[refIndex] = {
                                                    ...updatedRefs[refIndex],
                                                    min_value: prevRange.min_value,
                                                    max_value: prevRange.max_value
                                                };
                                                setRefRange(updatedRefs);
                                            }}
                                        >
                                            <IoCopyOutline />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className='remove-btn'
                                        onClick={() => removeMainRefRange(refIndex)}
                                        style={{ background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '25px', height: '25px', cursor: 'pointer' }}
                                    > - </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                className='add-btn'
                                style={{ width: '100%', marginTop: '5px' }}
                                onClick={addMainRefRange}
                            > + Add Range </button>
                        </div>

                        {type !== 'global' && (
                            <div className='pricing-inputcontainer'>
                                <span>Parameter</span>
                                {tests.map((testItem, testIndex) => (
                                    <div
                                        key={testIndex}
                                        style={{
                                            position: 'relative',
                                            border: '1px solid #E3E3E3',
                                            padding: '25px 15px 15px 15px',
                                            borderRadius: '8px',
                                            marginBottom: '15px',
                                            backgroundColor: '#fafafa'
                                        }}
                                    >
                                        <TestParameterRow
                                            item={testItem}
                                            parentIndex={testIndex}
                                            tests={tests}
                                            handleTestFieldChange={handleTestFieldChange}
                                            handleNestedRefChange={handleNestedRefChange}
                                            addNestedRefRange={addNestedRefRange}
                                            removeNestedRefRange={removeNestedRefRange}
                                            removeTestRow={removeTestRow}
                                            handleAddSubTest={handleAddSubTest}
                                            specificTestOptions={specificTestOptions}
                                            valueOptions={valueOptions}
                                            optionsList={optionsList}
                                            error={error.tests}
                                        />

                                        <div style={{ marginLeft: '30px', marginTop: '10px' }}>
                                            {testItem.sub_tests && testItem.sub_tests.length > 0 && (
                                                <div style={{ borderLeft: '3px solid #ccc', paddingLeft: '15px', marginBottom: '10px' }}>
                                                    <h6 style={{ fontSize: '14px', color: '#666', fontWeight: '600' }}>Subparameters</h6>
                                                    {testItem.sub_tests.map((subItem, subIndex) => (
                                                        <TestParameterRow
                                                            key={subIndex}
                                                            item={subItem}
                                                            parentIndex={testIndex}
                                                            subIndex={subIndex}
                                                            tests={tests}
                                                            handleTestFieldChange={handleTestFieldChange}
                                                            handleNestedRefChange={handleNestedRefChange}
                                                            addNestedRefRange={addNestedRefRange}
                                                            removeNestedRefRange={removeNestedRefRange}
                                                            removeTestRow={removeTestRow}
                                                            specificTestOptions={specificTestOptions}
                                                            valueOptions={valueOptions}
                                                            optionsList={optionsList}
                                                            error={error.tests}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                className='add-btn'
                                                onClick={() => handleAddSubTest(testIndex)}
                                            >
                                                {testItem.sub_tests && testItem.sub_tests.length > 0
                                                    ? "+ Add Another Subparameter Test"
                                                    : "+ Add New Subparameter Test"
                                                }
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                {error.tests && <p style={{ color: 'red', margin: '5px 0 0 0' }}>{error.tests}</p>}

                                <button
                                    type="button"
                                    className='add-btn'
                                    onClick={addTestRow}
                                    style={{ marginTop: '10px' }}
                                >
                                    {tests.length > 0 ? "+ Add Another Parameter" : "Add New Parameter"}
                                </button>
                            </div>
                        )}
                    </>
                )}

                {(selected === 'panels' || selected === 'testgroup') && (
                    <div className='pricing-inputcontainer'>
                        <span>Tests</span>
                        <Select
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    width: '100%',
                                    minHeight: '44px',
                                    border: error.tests ? '1px solid red' : '1px solid #E3E3E3',
                                }),
                                menu: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                }),
                                menuList: (provided) => ({
                                    ...provided,
                                    maxHeight: '200px',
                                    zIndex: 9999,
                                }),
                            }}
                            isMulti
                            value={tests}
                            onChange={handleTestChange}
                            options={testsList}
                            placeholder="Select Tests"
                            isClearable
                        />
                        {error.tests && <p style={{ color: 'red', margin: '5px 0 0 0' }}>{error.tests}</p>}
                    </div>
                )}

                {selected === 'testgroup' && (
                    <div className='pricing-inputcontainer'>
                        <span>Panels</span>
                        <Select
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    width: '100%',
                                    minHeight: '44px',
                                    border: error.panels ? '1px solid red' : '1px solid #E3E3E3',
                                }),
                                menu: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                }),
                                menuList: (provided) => ({
                                    ...provided,
                                    maxHeight: '200px',
                                    zIndex: 9999,
                                }),
                            }}
                            isMulti
                            value={panels}
                            onChange={handlePanelChange}
                            options={panelsList}
                            placeholder="Select Panels"
                            isClearable
                        />
                        {error.panels && <p style={{ color: 'red', margin: '5px 0 0 0' }}>{error.panels}</p>}
                    </div>
                )}
                {/* button */}
                <button
                    type="button"
                    className='add-btn'
                    style={{ margin: "auto auto 10px auto" }}
                    onClick={handleSubmit}
                    disabled={isLoading}
                >
                    {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </div>
    );
};

export default TestDetails;